import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["icon"]
  static values = {
    lightIcon: { type: String, default: '◐' },
    darkIcon: { type: String, default: '◑' }
  }

  // Lifecycle methods
  connect() {
    this.darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)')
    this.initializeTheme()
    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  // Public methods
  toggle() {
    const currentTheme = document.documentElement.getAttribute('data-bs-theme')
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark'
    this.setTheme(newTheme, true)
  }

  // Private methods
  initializeTheme() {
    const savedTheme = this.getStoredTheme()
    const systemPreference = this.darkModeQuery.matches ? 'dark' : 'light'
    const theme = !savedTheme || savedTheme === 'auto' ? systemPreference : savedTheme
    this.setTheme(theme, false)
  }

  setupEventListeners() {
    this.boundHandleSystemThemeChange = (e) => this.handleSystemThemeChange(e)
    this.boundHandleTurboLoad = () => this.handleTurboLoad()
    this.darkModeQuery.addEventListener('change', this.boundHandleSystemThemeChange)
    document.addEventListener('turbo:load', this.boundHandleTurboLoad)
  }

  removeEventListeners() {
    this.darkModeQuery?.removeEventListener('change', this.boundHandleSystemThemeChange)
    document.removeEventListener('turbo:load', this.boundHandleTurboLoad)
  }

  handleTurboLoad() {
    const currentTheme = document.documentElement.getAttribute('data-bs-theme')
    if (currentTheme) {
      this.setTheme(currentTheme, false)
    }
  }

  handleSystemThemeChange(e) {
    if (this.getStoredTheme() === 'auto') {
      this.setTheme(e.matches ? 'dark' : 'light', false)
    }
  }

  setTheme(theme, save = true) {
    document.documentElement.setAttribute('data-bs-theme', theme)

    if (save) {
      this.saveTheme(theme)
    }

    if (this.hasIconTarget) {
      this.iconTarget.textContent = theme === 'dark' ? this.darkIconValue : this.lightIconValue
    }
  }

  getStoredTheme() {
    try {
      return localStorage.getItem('theme')
    } catch (e) {
      console.warn('Unable to access localStorage:', e)
      return null
    }
  }

  saveTheme(theme) {
    try {
      localStorage.setItem('theme', theme)
    } catch (e) {
      console.warn('Unable to save theme preference:', e)
    }
  }
}

